import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { SidePanelProfile } from 'src/graphql/entities/side-panel-personalization';

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  profiles: SidePanelProfile[];
  activeProfileId: string | null;
  isDraft: boolean;
  onSelect: (profileId: string) => void;
  onDelete: (profileId: string) => void;
  onSaveAs: () => void;
}

const PROFILE_SOURCE_META: Record<
  SidePanelProfile['source'],
  { icon: string; hint: string | null }
> = {
  builtin: { icon: 'layout-dashboard', hint: 'The standard ProVision layout.' },
  group: { icon: 'users', hint: 'Assigned to your group' },
  user: { icon: 'bookmark', hint: null },
};

const menuItemSx = {
  borderRadius: 1.5,
  mx: 1,
  my: 0.25,
  alignItems: 'center',
  '&.Mui-selected': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'primary.dark' },
    '& .MuiListItemIcon-root': { color: 'inherit' },
    '& .MuiListItemText-secondary': { color: 'inherit', opacity: 0.85 },
  },
} as const;

const ActiveCheck = () => (
  <DynamicIcon name="check" library="lucide" size={18} sx={{ ml: 1, flexShrink: 0 }} />
);

export const ProfileMenu = ({
  anchorEl,
  onClose: close,
  profiles,
  activeProfileId,
  isDraft,
  onSelect,
  onDelete,
  onSaveAs,
}: ProfileMenuProps) => {
  const [saveAsRequested, setSaveAsRequested] = useState(false);

  // The save step hides the step this menu lives in, so a menu still fading out would never
  // finish and its invisible backdrop would keep swallowing clicks; open the step once it is gone.
  const handleExited = () => {
    if (saveAsRequested) {
      setSaveAsRequested(false);
      onSaveAs();
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={close}
      slotProps={{
        paper: { sx: { width: 380, maxWidth: '90vw' } },
        transition: { onExited: handleExited },
      }}
      data-testid="personalize-side-panel-profile-menu"
    >
      {isDraft ? (
        <MenuItem disabled selected sx={menuItemSx}>
          <ListItemIcon>
            <DynamicIcon name="pencil" library="lucide" size={20} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={600}>
                Custom
              </Typography>
            }
            secondary={activeProfileId ? 'Unsaved changes' : 'Unsaved view'}
          />
          <ActiveCheck />
        </MenuItem>
      ) : null}
      {profiles.map((profile) => {
        const isActive = !isDraft && profile.id === activeProfileId;
        const meta = PROFILE_SOURCE_META[profile.source];

        return (
          <MenuItem
            key={profile.id}
            data-testid={`personalize-side-panel-profile-option-${profile.id}`}
            selected={isActive}
            onClick={() => {
              onSelect(profile.id);
              close();
            }}
            sx={menuItemSx}
          >
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <DynamicIcon name={meta.icon} library="lucide" size={20} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={600}>
                  {profile.name}
                </Typography>
              }
              secondary={meta.hint}
            />
            {isActive ? <ActiveCheck /> : null}
            {profile.source === 'user' ? (
              <IconButton
                size="small"
                edge="end"
                aria-label={`Delete profile ${profile.name}`}
                data-testid={`personalize-side-panel-delete-profile-${profile.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(profile.id);
                  close();
                }}
                sx={{ ml: 1, p: 0.25 }}
              >
                <DynamicIcon name="trash-2" library="lucide" size={18} />
              </IconButton>
            ) : null}
          </MenuItem>
        );
      })}
      <Divider />
      <MenuItem
        data-testid="personalize-side-panel-save-profile"
        onClick={() => {
          setSaveAsRequested(true);
          close();
        }}
        sx={{ ...menuItemSx, color: 'primary.main' }}
      >
        <ListItemIcon sx={{ color: 'inherit' }}>
          <DynamicIcon name="bookmark-plus" library="lucide" size={20} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight={600}>
              Save current as profile…
            </Typography>
          }
        />
      </MenuItem>
    </Menu>
  );
};
